import React from "react";
import { ApiHelper, PlaylistMessageInterface, ArrayHelper, PendingCommand, MessageInterface, PlaylistInterface, PlaylistMessageConditionInterface } from "../../helpers";
import { PlaylistMessage } from "./PlaylistMessage";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";

interface Props {
  playlistId: number,
  saveCallback: () => void,
  pendingCommand: PendingCommand,
  sendCommand: (cmd: PendingCommand) => void
}

export const PlaylistMessages: React.FC<Props> = props => {

  const [loadedPlaylistMessages, setLoadedPlaylistMessages] = React.useState<PlaylistMessageInterface[]>([]);
  const [playlistMessages, setPlaylistMessages] = React.useState<PlaylistMessageInterface[]>([]);
  const [deleteIds, setDeleteIds] = React.useState<number[]>([]);

  const params = new URLSearchParams(window.location.search);

  const init = () => { loadData() }

  const loadData = async () => {
    if (props.playlistId > 0) {
      const data = await ApiHelper.get<PlaylistMessageInterface[]>("/playlistMessages/playlist/" + props.playlistId) || [];
      if (data.length > 0) {
        const messageIds = ArrayHelper.getIds(data, "messageId");
        const playlistIds = ArrayHelper.getIds(data, "childPlaylistId");

        const messages = (messageIds.length > 0) ? await ApiHelper.get<MessageInterface[]>("/messages?ids=" + messageIds.join(",")) || [] : [];
        const playlists = (playlistIds.length > 0) ? await ApiHelper.get<PlaylistInterface[]>("/playlists?ids=" + playlistIds.join(",")) || [] : [];
        let allConditions = (params.get("mode") === "playlist") ? await ApiHelper.get<PlaylistMessageConditionInterface[]>("/playlistMessageConditions/playlist/" + props.playlistId) || [] : [];
        if (!allConditions) allConditions = [];
        ArrayHelper.sort(allConditions, "questionKeyName", false);
        for (let pm of data) {
          if (pm.messageId > 0) pm.message = ArrayHelper.getOne(messages, "id", pm.messageId);
          else if (pm.childPlaylistId) pm.childPlaylist = ArrayHelper.getOne(playlists, "id", pm.childPlaylistId);
          pm.conditions = ArrayHelper.getAll(allConditions, "playlistMessageId", pm.id);
        }
      }
      setDeleteIds([]);

      setPlaylistMessages(data);

      const loadedTemp: PlaylistMessageInterface[] = [];
      data.forEach(d => loadedTemp.push({ ...d }));
      setLoadedPlaylistMessages(loadedTemp);

    }
  }

  const handlePlaylistMessageCommand = (cmd: PendingCommand) => {
    if (cmd.action === "remove") {
      const index = parseInt(cmd.parameter as string);
      const pm = playlistMessages[index];

      if (pm.id && deleteIds.indexOf(pm.id) === -1) {
        let deleteIdsCopy: number[] = [...deleteIds];
        deleteIdsCopy.push(pm.id)
        setDeleteIds(deleteIdsCopy);
      }

      const pms = [...playlistMessages];
      pms.splice(index, 1);
      setPlaylistMessages(pms);
    } else if (cmd.action === "subset" || cmd.action === "conditions") props.sendCommand(cmd);

  }

  const getRows = () => {
    const result: React.ReactElement[] = [];

    let idx = 0;
    playlistMessages?.forEach(pm => {
      //result.push(getRow(pm, idx))
      if (!pm.message || !pm.message.archived) result.push(<PlaylistMessage key={"pm_" + pm.id + "_" + pm.sortOrder + "_" + pm.messageId} index={idx} playlistMessage={pm} sendCommand={handlePlaylistMessageCommand} />)

      idx++;
    });
    return result;
  }

  const saveChanged = async (playlistMessages: PlaylistMessageInterface[]) => {
    const toSave: PlaylistMessageInterface[] = []
    for (let i = playlistMessages.length - 1; i >= 0; i--) {
      const pm = playlistMessages[i];
      if (!pm.id) {
        toSave.push(pm);
        playlistMessages.splice(i, 0);
      } else {
        const loaded = ArrayHelper.getOne(loadedPlaylistMessages, "id", pm.id);
        if (!loaded || JSON.stringify(loaded) !== JSON.stringify(pm)) {
          toSave.push(pm);
          playlistMessages.splice(i, 0);
        }
      }
    }

    if (toSave.length > 0) {
      let batches = ArrayHelper.makeBatches(toSave, 50);
      for (let batch of batches) {
        let result: PlaylistMessageInterface[] = await ApiHelper.post("/playlistMessages", batch);
        playlistMessages = playlistMessages.concat(result);
      }
    }
    return playlistMessages;
  }

  const handleSave = async (playlistId: number) => {
    playlistMessages.forEach(pm => pm.playlistId = playlistId);
    let promises: Promise<any>[] = []
    deleteIds.forEach(id => { promises.push(ApiHelper.delete("/playlistMessages/" + id)) });
    if (promises.length > 0) await Promise.all(promises);
    promises = [];

    if (playlistMessages.length > 0) {
      for (let i = 0; i < playlistMessages.length; i++) playlistMessages[i].sortOrder = i + 1;
      const result: PlaylistMessageInterface[] = await saveChanged(playlistMessages); //ApiHelper.post("/playlistMessages", playlistMessages);
      for (let i = 0; i < playlistMessages.length; i++) {
        let pm = playlistMessages[i];
        if (pm.conditions?.length > 0) {
          for (let c of pm.conditions) {
            if (!c.id || c.id === 0) {
              c.playlistMessageId = result[i].id;
              await ApiHelper.post("/playlistMessageConditions", [c]);
            }
          }
        }
        if (pm.deleteConditionIds?.length > 0) {
          for (let id of pm.deleteConditionIds) await ApiHelper.delete("/playlistMessageConditions/" + id);
        }

      }
    }
    if (props.playlistId > 0) loadData();
    props.saveCallback()
  }

  const handleAddMessage = (message: MessageInterface) => {
    const pms = [...playlistMessages];
    const pm: PlaylistMessageInterface = { playlistId: props.playlistId, message: message, messageId: message.id, sortOrder: pms.length + 1, id: 0 }
    pms.push(pm);
    setPlaylistMessages(pms);
  }

  const handleAddPlaylist = (childPlaylist: PlaylistInterface) => {
    const pms = [...playlistMessages];
    const pm: PlaylistMessageInterface = { playlistId: props.playlistId, childPlaylist: childPlaylist, childPlaylistId: childPlaylist.id, childPlaylistEnd: 999, childPlaylistStart: 1, sortOrder: pms.length + 1, id: 0 }
    pms.push(pm);
    setPlaylistMessages(pms);
  }

  const handleUpdateSubset = (playlistMessage: PlaylistMessageInterface) => {
    const pms = [...playlistMessages];
    let pm: PlaylistMessageInterface = ArrayHelper.getOne(pms, "sortOrder", playlistMessage.sortOrder);
    if (playlistMessage.id > 0) pm = ArrayHelper.getOne(pms, "id", playlistMessage.id);
    pm.childPlaylistStart = playlistMessage.childPlaylistStart;
    pm.childPlaylistEnd = playlistMessage.childPlaylistEnd;
    setPlaylistMessages(pms);
  }

  const handleUpdateConditions = (playlistMessage: PlaylistMessageInterface) => {
    const pms = [...playlistMessages];
    let pm: PlaylistMessageInterface = ArrayHelper.getOne(pms, "sortOrder", playlistMessage.sortOrder);
    if (playlistMessage.id > 0) pm = ArrayHelper.getOne(pms, "id", playlistMessage.id);
    pm.conditions = playlistMessage.conditions;
    pm.deleteConditionIds = playlistMessage.deleteConditionIds;
    setPlaylistMessages(pms);
  }

  const handleParentCommand = () => {
    if (props.pendingCommand) {
      switch (props.pendingCommand.action) {
        case "save":
          handleSave(parseInt(props.pendingCommand.parameter as string, 0));
          break;
        case "addMessage":
          handleAddMessage(JSON.parse(props.pendingCommand.parameter as string));
          break;
        case "addPlaylist":
          handleAddPlaylist(JSON.parse(props.pendingCommand.parameter as string));
          break;
        case "updateSubset":
          handleUpdateSubset(props.pendingCommand.parameter as PlaylistMessageInterface);
          break;
        case "updateConditions":
          handleUpdateConditions(props.pendingCommand.parameter as PlaylistMessageInterface);
          break;
      }
    }
  }

  React.useEffect(init, [props.playlistId]); // eslint-disable-line
  React.useEffect(handleParentCommand, [props.pendingCommand]); // eslint-disable-line


  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const pms = [...playlistMessages]
    const [item] = pms.splice(result.source.index, 1);
    pms.splice(result.destination.index, 0, item);
    setPlaylistMessages(pms);
  }

  const getContainerStyle = (data: string) => {
    if (data) return { paddingBottom: 100 }
    else return {}
  }


  return (
    <>
      <div className="section">Playlist Messages</div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="todo" >
          {(provided, snapshot) => (
            <div id="playlistMessages" className="list-group" {...provided.droppableProps} ref={provided.innerRef} style={getContainerStyle(snapshot.draggingFromThisWith)} >
              <>{getRows()}</>
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <p><i>Drag to reorder messages.</i></p>
    </>
  );
};